import IntlMsgFormat from 'intl-messageformat'
import * as React from 'react'
import {
  type ReactNode,
  useMemo,
  isValidElement,
  useCallback,
  useRef,
} from 'react'

import {
  fallbackTranslationsNFFleet,
  fallbackTranslations,
} from '@ngb-frontend/shared/mocks'
import { SCBrand } from '@ngb-frontend/shared/types'

import { getSitecoreBrand } from './utils/api'
import { useFetchContent } from './utils/useFetchContent'
import { useLocalization } from '../LocaleContext/LocaleContext'

import type { ContentValue, TContent } from '@ngb-frontend/shared/types'
import type { FormatXMLElementFn } from 'intl-messageformat'

export interface ContentProviderProps {
  children?: ReactNode
}

interface ContentContextProps<T extends ContentValue = TContent> {
  content?: T
  fallbackContent?: T
  isContentReady: boolean
}

export type RichTextFormatFn = FormatXMLElementFn<ReactNode>
interface ContentOptions<
  T extends RichTextFormatFn | string = string | RichTextFormatFn,
> {
  variables?: Record<string, T>
  separator?: string
  fallback?: string
}

export const DEFAULT_CONTENT_LOCALE = 'en-GB'

// Formats one or many messages into a single string. Used in almost all cases.
const formatToStringText = (
  msg: string | string[],
  variables: ContentOptions<string>['variables'],
  separator = ',',
) => {
  return (!Array.isArray(msg) ? [msg] : msg)
    .map((part) => String(new IntlMsgFormat(part).format(variables)))
    .join(separator)
}

// Formats one or many xml messages into an array of rich content (html links etc...)
const formatToRichText = (
  msg: string | string[],
  variables: ContentOptions['variables'],
) => {
  if (!Array.isArray(msg)) {
    const richContent = new IntlMsgFormat(msg).format(variables) || ''

    return !React.isValidElement(richContent) && !Array.isArray(richContent)
      ? String(richContent)
      : richContent
  }

  return msg.map((part) => new IntlMsgFormat(part).format(variables)).flat()
}

const ContentContext = React.createContext<ContentContextProps>({
  content: undefined,
  fallbackContent: undefined,
  isContentReady: false,
})

export const ContentProvider: React.FC<ContentProviderProps> = ({
  children,
}) => {
  const { locale, country, availableLocales } = useLocalization()
  const contentBrand = getSitecoreBrand(country)
  const fallbackContent = useRef<TContent>(
    contentBrand === SCBrand.NFFleet
      ? fallbackTranslationsNFFleet
      : fallbackTranslations,
  )

  const { contentMap, isContentReady } = useFetchContent(
    contentBrand,
    availableLocales,
    fallbackContent.current,
  )

  const content = useMemo(
    () =>
      isContentReady
        ? contentMap?.[locale] ?? fallbackContent.current
        : fallbackContent.current,
    [contentMap, isContentReady, locale, fallbackContent],
  )

  const contextConfig = useMemo(
    () => ({
      content,
      fallbackContent: fallbackContent.current,
      isContentReady,
    }),
    [content, fallbackContent, isContentReady],
  )

  return (
    <ContentContext.Provider value={contextConfig}>
      {children}
    </ContentContext.Provider>
  )
}

export const findByPath = (
  path: string,
  content: ContentValue,
): ContentValue | undefined => {
  // @ts-ignore
  return path.split('.').reduce((acc, curr) => acc?.[curr], content)
}

export const findContent = (
  path: string,
  fallbackPath = '',
  content: ContentValue,
  fallbackContent?: ContentValue,
): ContentValue | undefined => {
  let message = findByPath(path, content)

  // Terminal state (covers cases when content should be left intentionally empty)
  if (!message && typeof message === 'string' && !fallbackContent) {
    return ''
  }

  // Alternate path fallback (same locale)
  if (!message && fallbackPath) {
    return findContent(fallbackPath, '', content, fallbackContent)
  }

  // Retry search in GLOBAL content if its not intentionally emptied and there's no fallback
  if (!message && fallbackContent) {
    message = findContent(path, fallbackPath, fallbackContent)
  }

  if (!message) {
    // eslint-disable-next-line no-console
    console.warn(`No content found for path "${path}"`) // NOSONAR
  }

  return message
}

export const useContentContext = (): Required<
  Pick<ContentContextProps<TContent>, 'content' | 'fallbackContent'> &
    Pick<ContentContextProps, 'isContentReady'>
> => {
  const ctx = React.useContext(ContentContext)

  if (!ctx.content) {
    throw new Error('useContent must be used within a ContentProvider')
  }

  return ctx as Required<
    Pick<ContentContextProps<TContent>, 'content' | 'fallbackContent'> &
      Pick<ContentContextProps, 'isContentReady'>
  >
}

export const useContent = () => {
  const ctx = useContentContext()

  // TODO: Take off all callbacks out of the hook and test
  const formatCore = useCallback(
    <T extends ContentOptions>(
      path: string,
      opts: T | undefined,
      cb: (
        path: string | string[],
        vars: T['variables'],
        separator: T['separator'],
      ) => string | ReactNode | ReactNode[],
    ) => {
      const { variables, fallback, separator } = opts || {}
      const message = findContent(
        path,
        fallback,
        ctx.content as ContentValue,
        ctx.fallbackContent as ContentValue,
      )
      // TODO: Revise strategy of fetching structures different to string | string[]
      if (!(Array.isArray(message) || typeof message === 'string')) {
        return path
      }

      const msgAsOneOrChunkedStrings = Array.isArray(message)
        ? message.filter((part): part is string => typeof part === 'string')
        : message

      const unformattedMessage = Array.isArray(msgAsOneOrChunkedStrings)
        ? msgAsOneOrChunkedStrings.join(separator)
        : msgAsOneOrChunkedStrings

      if (!variables) return unformattedMessage

      try {
        return cb(msgAsOneOrChunkedStrings, variables, separator)
      } catch (e) {
        return unformattedMessage
      }
    },
    [ctx.content, ctx.fallbackContent],
  )

  const format = useMemo(() => {
    const f = (path: string, opts?: ContentOptions<string>) => {
      return `${formatCore(path, opts, formatToStringText)}`
    }

    f.rich = (path: string, opts?: ContentOptions) => {
      const richText = formatCore(path, opts, formatToRichText)

      if (!Array.isArray(richText)) return richText

      return richText.map((t, i) =>
        isValidElement(t) ? <React.Fragment key={i}>{t}</React.Fragment> : t,
      )
    }

    return f
  }, [formatCore])

  return format
}
